import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  Loader2,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import knowledgeGapsService, {
  type KGReportCategory,
  type KGStartTrainingSessionResponse,
  type KGSummariesParams,
  type KGSummaryResponse,
  type KGResolutionType,
} from '@/lib/api/knowledgeGaps';
import { TrainingAgentDialog } from '@/components/onboarding/TrainingAgentDialog';
import { DataSurface } from '@/components/platform/DataSurface';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  LOW_CONFIDENCE_THRESHOLD,
  PAGE_SIZE,
  POLL_BACKOFF_AFTER_MS,
  POLL_BACKOFF_MS,
  POLL_INTERVAL_MS,
  POLL_SLOW_NOTICE_AFTER_MS,
  RESOLUTION_OPTIONS,
  SORT_OPTIONS,
  STATUS_FILTER_TO_PARAM,
  STATUS_OPTIONS,
  type SortBy,
  type StatusFilter,
} from './active-gaps/constants';
import { getCategoryMeta, getFrequencyMeta, getStatusBadge } from './active-gaps/helpers';
import { GapDetail } from './active-gaps/GapDetail';

// ─── Main component ─────────────────────────────────────────────────────────

interface ActiveGapsTabProps {
  orgId: string;
}

function summaryFreshness(summary: KGSummaryResponse): number {
  const timestamps = [
    summary.updated_at,
    summary.resolved_at,
    summary.dismissed_at,
    summary.created_at,
  ];
  return timestamps.reduce((latest, value) => {
    if (!value) return latest;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? Math.max(latest, parsed) : latest;
  }, 0);
}

function isTerminalSummary(summary: KGSummaryResponse): boolean {
  return summary.status === 'resolved' || summary.status === 'dismissed';
}

export function ActiveGapsTab({ orgId }: ActiveGapsTabProps) {
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [resolutionFilter, setResolutionFilter] = useState<'all' | KGResolutionType>(
    'all',
  );
  const [sortBy, setSortBy] = useState<SortBy>('occurrence_count_desc');
  const [page, setPage] = useState(1);
  const [selectedSummaryId, setSelectedSummaryId] = useState<string | null>(null);

  // Tracks when each in-flight batch started polling (ms since epoch), for
  // backoff + slow-notice behavior.
  const [pollStartedAtByBatch, setPollStartedAtByBatch] = useState<
    Record<string, number>
  >({});

  const invalidateSummaries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['knowledge-gaps-summaries', orgId],
    });
    // Also refresh the detail fallback so terminal transitions
    // (resolving_via_prompt → resolved) flip State 2 → State 3 in place
    // even when the row has dropped off the list.
    queryClient.invalidateQueries({
      queryKey: ['knowledge-gap-summary-detail', orgId],
    });
  }, [orgId, queryClient]);

  // ── Queries ─────────────────────────────────────────────────────────────

  const {
    data: report,
    isError: reportError,
    refetch: refetchReport,
  } = useQuery({
    queryKey: ['knowledge-gaps-report', orgId],
    queryFn: async () => {
      const res = await knowledgeGapsService.getReport(orgId);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load report');
      }
      return res.data;
    },
    enabled: !!orgId,
  });

  const {
    data: summariesData,
    isLoading: summariesLoading,
    isError: summariesError,
    refetch: refetchSummaries,
  } = useQuery({
    queryKey: [
      'knowledge-gaps-summaries',
      orgId,
      category,
      statusFilter,
      resolutionFilter,
      sortBy,
      page,
    ],
    queryFn: async ({ signal }) => {
      // Status filter drives the `status` param. Default ('active') keeps the
      // Open Gaps queue aligned with the unresolved gap badge.
      const params: KGSummariesParams = {
        status: STATUS_FILTER_TO_PARAM[statusFilter],
        sort_by: sortBy,
        page,
        limit: PAGE_SIZE,
      };
      if (category !== 'all') {
        params.category = category;
      }
      if (resolutionFilter !== 'all') {
        params.resolution_type = resolutionFilter;
      }
      const res = await knowledgeGapsService.getSummaries(orgId, params, {
        signal,
      });
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load summaries');
      }
      return res.data;
    },
    enabled: !!orgId,
    staleTime: 0,
  });

  const summariesRaw: KGSummaryResponse[] = useMemo(
    () => summariesData?.summaries ?? [],
    [summariesData?.summaries],
  );
  // Server-side filter since Fix 7 — no client post-filter.
  const summaries = summariesRaw;

  // ── In-flight batch polling ─────────────────────────────────────────────
  // Any summary with status=resolving_via_prompt / resolving_via_product_capture
  // or a non-null in_progress_prompt_update / in_flight_capture drives a
  // periodic refetch. Cadence is 3s until 2 min in, then 10s. Paused when
  // the tab is hidden (Page Visibility API).

  const inFlightBatches = useMemo(() => {
    const ids: { summaryId: string; batchId: string }[] = [];
    for (const s of summariesRaw) {
      const isPromptInFlight =
        s.status === 'resolving_via_prompt' ||
        s.in_progress_prompt_update !== null;
      const isCaptureInFlight =
        s.status === 'resolving_via_product_capture' ||
        s.in_flight_capture !== null;
      if (isPromptInFlight) {
        ids.push({
          summaryId: s.id,
          batchId: s.in_progress_prompt_update?.batch_id ?? s.id,
        });
      } else if (isCaptureInFlight) {
        // Use the linked Demo Studio session id as the tracking key when
        // present so the polling-start timestamp survives a session-status
        // transition (launching → active → processing → complete) without
        // resetting the backoff window. Falls back to the summary id when
        // the capture state hasn't hydrated yet.
        ids.push({
          summaryId: s.id,
          batchId: s.in_flight_capture?.session_id ?? s.id,
        });
      }
    }
    return ids;
  }, [summariesRaw]);

  useEffect(() => {
    if (inFlightBatches.length === 0) return;
    const startedAt = Date.now();
    setPollStartedAtByBatch((prior) => {
      // Only return a new reference when we actually add a batch — otherwise
      // the polling effect (which depends on this state) would tear down and
      // restart on every poll cycle.
      let next: Record<string, number> | null = null;
      for (const { batchId } of inFlightBatches) {
        if (!(batchId in prior)) {
          if (next === null) next = { ...prior };
          next[batchId] = startedAt;
        }
      }
      return next ?? prior;
    });
  }, [inFlightBatches]);

  useEffect(() => {
    if (inFlightBatches.length === 0) return;
    let timeoutId: number | undefined;
    let cancelled = false;

    function getInterval(): number {
      const now = Date.now();
      const anyOld = inFlightBatches.some(({ batchId }) => {
        const startedAt = pollStartedAtByBatch[batchId];
        return startedAt && now - startedAt > POLL_BACKOFF_AFTER_MS;
      });
      return anyOld ? POLL_BACKOFF_MS : POLL_INTERVAL_MS;
    }

    function schedule(): void {
      if (cancelled) return;
      timeoutId = window.setTimeout(tick, getInterval());
    }

    function tick(): void {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        // Tab hidden — skip this tick, resume on visibilitychange.
        return;
      }
      if (
        queryClient.isFetching({ queryKey: ['knowledge-gaps-summaries', orgId] }) === 0
      ) {
        invalidateSummaries();
      }
      schedule();
    }

    function onVisibility(): void {
      if (cancelled) return;
      if (document.visibilityState === 'visible') {
        if (
          queryClient.isFetching({ queryKey: ['knowledge-gaps-summaries', orgId] }) === 0
        ) {
          invalidateSummaries();
        }
        if (timeoutId !== undefined) {
          window.clearTimeout(timeoutId);
        }
        schedule();
      }
    }

    schedule();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [inFlightBatches, pollStartedAtByBatch, invalidateSummaries, orgId, queryClient]);

  // ── Mutations ───────────────────────────────────────────────────────────

  const resolveManualMutation = useMutation({
    mutationFn: async (summaryId: string) => {
      const res = await knowledgeGapsService.resolveSummary(orgId, summaryId);
      if (!res.success) {
        throw new Error(res.error || 'Failed to resolve summary');
      }
      return res.data;
    },
    onSuccess: () => {
      setSelectedSummaryId(null);
      invalidateSummaries();
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-report', orgId] });
      toast({ title: 'Summary marked as resolved' });
    },
    onError: () => {
      toast({
        title: 'Failed to resolve summary',
        description: 'We could not mark this gap as resolved. Try again.',
        variant: 'destructive',
      });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async (summaryId: string) => {
      const res = await knowledgeGapsService.dismissSummary(orgId, summaryId);
      if (!res.success) {
        throw new Error(res.error || 'Failed to dismiss summary');
      }
      return res.data;
    },
    onSuccess: () => {
      setSelectedSummaryId(null);
      invalidateSummaries();
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-report', orgId] });
      toast({ title: 'Summary dismissed' });
    },
    onError: () => {
      toast({
        title: 'Failed to dismiss summary',
        description: 'We could not dismiss this gap. Try again.',
        variant: 'destructive',
      });
    },
  });

  const resolveViaPromptMutation = useMutation({
    mutationFn: async (summaryId: string) => {
      const res = await knowledgeGapsService.resolveWithPromptUpdate(
        orgId,
        summaryId,
      );
      if (!res.success) {
        throw new Error(res.error || 'Failed to start prompt update');
      }
      return res.data;
    },
    onSuccess: () => {
      invalidateSummaries();
      toast({ title: 'Prompt optimization started' });
    },
    onError: () => {
      toast({
        title: 'Could not start prompt update',
        description: 'We could not start that update. Try again.',
        variant: 'destructive',
      });
    },
  });

  // Training-session resolution flow (for unanswered_question gaps).
  // The mutation creates a session server-side and returns a bootstrap
  // payload the dialog seeds itself with directly. Dialog close
  // re-invalidates the summaries query so the gap card refreshes to its
  // resolved state without a manual refetch.
  const [trainingPreset, setTrainingPreset] =
    useState<KGStartTrainingSessionResponse | null>(null);

  const startTrainingMutation = useMutation({
    mutationFn: async (summaryId: string) => {
      const res = await knowledgeGapsService.startTrainingSession(
        orgId,
        summaryId,
      );
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to start training session');
      }
      return res.data;
    },
    onSuccess: (data) => {
      setTrainingPreset(data);
    },
    onError: () => {
      toast({
        title: 'Could not start training session',
        description: 'We could not start the interview. Try again.',
        variant: 'destructive',
      });
    },
  });

  // ── Derived ─────────────────────────────────────────────────────────────

  const totalSummaries = summariesData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalSummaries / PAGE_SIZE));
  const categories = report?.categories ?? [];
  const listCountLabel =
    statusFilter === 'active'
      ? `${totalSummaries} open`
      : statusFilter === 'all'
      ? `${totalSummaries} shown`
      : `${totalSummaries} ${statusFilter}`;
  const isLoading = summariesLoading;
  const isError = reportError || summariesError;

  // Primary source: the paginated list. If the selected summary transitioned
  // to a status excluded from the list filter (e.g. resolved), fall back to
  // the single-summary detail endpoint so the panel can render State 3/4
  // without the row vanishing.
  const selectedFromList =
    summaries.find((s) => s.id === selectedSummaryId) ?? null;

  const { data: selectedFromDetail } = useQuery({
    queryKey: ['knowledge-gap-summary-detail', orgId, selectedSummaryId],
    queryFn: async ({ signal }) => {
      const res = await knowledgeGapsService.getSummary(
        orgId,
        selectedSummaryId!,
        { signal },
      );
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load summary');
      }
      return res.data;
    },
    enabled: !!orgId && !!selectedSummaryId && !selectedFromList,
    staleTime: 30_000,
  });

  const selectedSummary: KGSummaryResponse | null = useMemo(() => {
    if (selectedFromList && selectedFromDetail) {
      if (isTerminalSummary(selectedFromDetail) && !isTerminalSummary(selectedFromList)) {
        return selectedFromDetail;
      }
      if (isTerminalSummary(selectedFromList) && !isTerminalSummary(selectedFromDetail)) {
        return selectedFromList;
      }
      return summaryFreshness(selectedFromDetail) > summaryFreshness(selectedFromList)
        ? selectedFromDetail
        : selectedFromList;
    }
    return selectedFromList ?? selectedFromDetail ?? null;
  }, [selectedFromList, selectedFromDetail]);

  // Keep the selected row visually consistent with the detail panel. This
  // covers both terminal transitions that drop off the current filter and
  // stale paginated-list data arriving after a single-summary update.
  const displaySummaries: KGSummaryResponse[] = useMemo(() => {
    if (!selectedSummary) return summaries;
    const index = summaries.findIndex((s) => s.id === selectedSummary.id);
    if (index === -1) return [selectedSummary, ...summaries];
    if (summaries[index] === selectedSummary) return summaries;
    const next = summaries.slice();
    next[index] = selectedSummary;
    return next;
  }, [summaries, selectedSummary]);

  const selectedSlowNotice = (() => {
    if (!selectedSummary) return false;
    const batchId =
      selectedSummary.in_progress_prompt_update?.batch_id ?? selectedSummary.id;
    const startedAt = pollStartedAtByBatch[batchId];
    return startedAt ? Date.now() - startedAt > POLL_SLOW_NOTICE_AFTER_MS : false;
  })();

  // One-time deep-link select. When the URL carries `?selected=<summary_id>`
  // (e.g. after a Demo Studio close-back redirect), seed the selection to
  // that gap so the user lands on the right detail panel. Consumed once,
  // then cleared from the URL so a subsequent click on a different gap
  // isn't overridden on re-render.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkConsumedRef = useRef(false);
  useEffect(() => {
    if (deepLinkConsumedRef.current) return;
    const requested = searchParams.get('selected');
    if (!requested) return;
    deepLinkConsumedRef.current = true;
    setSelectedSummaryId(requested);
    const next = new URLSearchParams(searchParams);
    next.delete('selected');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Auto-select first summary when data loads
  useEffect(() => {
    if (summaries.length > 0 && !selectedSummaryId) {
      setSelectedSummaryId(summaries[0].id);
    }
  }, [summaries, selectedSummaryId]);

  // Reset selection when filters change
  const handleCategoryChange = (value: string) => {
    setCategory(value);
    setPage(1);
    setSelectedSummaryId(null);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value as StatusFilter);
    setPage(1);
    setSelectedSummaryId(null);
  };

  const handleResolutionChange = (value: string) => {
    setResolutionFilter(value as 'all' | KGResolutionType);
    setPage(1);
    setSelectedSummaryId(null);
  };

  const handleSortChange = (value: string) => {
    setSortBy(value as SortBy);
    setPage(1);
    setSelectedSummaryId(null);
  };

  // ── Error state ─────────────────────────────────────────────────────────

  if (isError && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">
          Failed to load knowledge gaps data.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            refetchReport();
            refetchSummaries();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Status</label>
          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Category</label>
          <Select value={category} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {categories.map((cat: KGReportCategory) => {
                const meta = getCategoryMeta(cat.category);
                return (
                  <SelectItem key={cat.category} value={cat.category}>
                    {meta.label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Resolution</label>
          <Select value={resolutionFilter} onValueChange={handleResolutionChange}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RESOLUTION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Sort By</label>
          <Select value={sortBy} onValueChange={handleSortChange}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Master-detail layout */}
      {isLoading ? (
        <DataSurface className="h-[calc(100vh-16rem)] min-h-[520px]">
          <div className="grid h-full lg:grid-cols-[340px_minmax(0,1fr)]">
          <div className="border-r p-3 space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-14 w-full rounded-md" />
            ))}
          </div>
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
          </div>
        </DataSurface>
      ) : displaySummaries.length === 0 ? (
        <EmptyState
          icon={Lightbulb}
          title="No knowledge gaps detected"
          description="Use Find Gaps to scan conversations for missing knowledge."
        />
      ) : (
        <DataSurface className="h-[calc(100vh-16rem)] min-h-[560px]">
          <div className="grid h-full lg:grid-cols-[340px_minmax(0,1fr)]">
          {/* Left: summary list */}
          <div className="flex min-h-0 flex-col border-b lg:border-b-0 lg:border-r">
            <div className="px-4 py-3 min-h-[52px] border-b flex items-center gap-3">
              <Lightbulb className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-foreground">Open gaps</h3>
              <span
                className="ml-auto rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                aria-label={listCountLabel}
                title={listCountLabel}
              >
                {listCountLabel}
              </span>
            </div>

            <div className="min-h-0 flex-1 overflow-auto divide-y">
              {displaySummaries.map((summary: KGSummaryResponse) => {
                const isSelected = selectedSummaryId === summary.id;
                const meta = getCategoryMeta(summary.category);
                const isInFlight =
                  summary.status === 'resolving_via_prompt' ||
                  summary.in_progress_prompt_update !== null;
                const statusBadge = getStatusBadge(summary.status);
                const showPromptPill =
                  summary.resolution_type === 'prompt_update';
                const lowConfidencePill =
                  showPromptPill &&
                  summary.resolution_type_confidence !== null &&
                  summary.resolution_type_confidence < LOW_CONFIDENCE_THRESHOLD;
                const frequency = getFrequencyMeta(summary.occurrence_count);

                return (
                  <button
                    key={summary.id}
                    type="button"
                    className={cn(
                      'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40',
                      isSelected && 'bg-muted/50',
                    )}
                    onClick={() => setSelectedSummaryId(summary.id)}
                  >
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {summary.topic}
                        </div>
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                          {summary.category !== 'missing_knowledge' && (
                            <span className="truncate">{meta.label}</span>
                          )}
                          {summary.source_type === 'simulated' && (
                            <Badge
                              variant="outline"
                              className="shrink-0 text-[10px] px-1 py-0 h-4 leading-none"
                            >
                              Simulated
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap mt-1">
                          <Badge
                            variant="outline"
                            className={cn('text-[10px] py-0 px-1.5 font-normal', frequency.className)}
                            aria-label={frequency.detail}
                            title={frequency.detail}
                          >
                            {frequency.label}
                          </Badge>
                          {statusBadge && (
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-[10px] py-0 px-1.5 font-normal inline-flex items-center gap-1',
                                statusBadge.className,
                              )}
                            >
                              <statusBadge.icon className="h-2.5 w-2.5" />
                              {statusBadge.label}
                            </Badge>
                          )}
                          {showPromptPill && (
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-[10px] py-0 px-1.5 font-normal',
                                lowConfidencePill &&
                                  'border-dashed border-muted-foreground',
                              )}
                            >
                              prompt update
                              {lowConfidencePill && <span className="ml-0.5">?</span>}
                            </Badge>
                          )}
                          {isInFlight && (
                            <Badge
                              variant="secondary"
                              className="text-[10px] py-0 px-1.5 font-normal"
                            >
                              <Loader2 className="h-2.5 w-2.5 mr-1 animate-spin" />
                              Optimizing
                            </Badge>
                          )}
                        </div>
                      </div>
                  </button>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="border-t px-4 py-2 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                      setSelectedSummaryId(null);
                    }}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => {
                      setPage((p) => Math.min(totalPages, p + 1));
                      setSelectedSummaryId(null);
                    }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right: detail panel */}
          <div className="min-h-0 overflow-hidden">
            {selectedSummary ? (
              <GapDetail
                orgId={orgId}
                summary={selectedSummary}
                onResolveManual={() =>
                  resolveManualMutation.mutate(selectedSummary.id)
                }
                onDismiss={() => dismissMutation.mutate(selectedSummary.id)}
                onResolveViaPrompt={() =>
                  resolveViaPromptMutation.mutate(selectedSummary.id)
                }
                onAnswerInTraining={() =>
                  startTrainingMutation.mutate(selectedSummary.id)
                }
                isResolvingManual={
                  resolveManualMutation.isPending &&
                  resolveManualMutation.variables === selectedSummary.id
                }
                isDismissing={
                  dismissMutation.isPending &&
                  dismissMutation.variables === selectedSummary.id
                }
                isStartingPromptUpdate={
                  resolveViaPromptMutation.isPending &&
                  resolveViaPromptMutation.variables === selectedSummary.id
                }
                isStartingTraining={
                  startTrainingMutation.isPending &&
                  startTrainingMutation.variables === selectedSummary.id
                }
                slowNotice={selectedSlowNotice}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                Select a gap to view details.
              </div>
            )}
          </div>
          </div>
        </DataSurface>
      )}

      {/* Gap-driven training session modal. Mounted only when the user has
          clicked "Answer in training" and the bootstrap payload has come
          back from the server. Dialog close clears the preset and refreshes
          the summaries list so the gap card flips to its resolved state. */}
      {trainingPreset && (
        <TrainingAgentDialog
          interviewer={{
            token: trainingPreset.interviewer_token,
            name: trainingPreset.interviewer_name,
            description: trainingPreset.interviewer_description,
            processing_status: 'completed',
          }}
          open={!!trainingPreset}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setTrainingPreset(null);
              invalidateSummaries();
              queryClient.invalidateQueries({
                queryKey: ['knowledge-gaps-report', orgId],
              });
            }
          }}
          mode="open_questions"
          presetSession={trainingPreset}
        />
      )}
    </div>
  );
}
